import { action, flow, makeObservable, observable } from 'mobx';
import api from '../utils/apiService';
import { apiFetcher } from '../utils/fetch';
import { improvedUpdateItems, updateItems } from './utils';

const paginationSchema = {
  page: 1,
  pageSize: 10,
  totalPages: 1,
  totalData: 0,
  hasNextPage: false,
  hasPrevPage: false
}

export default class Invoice {
  constructor() {
    this.selected = [];
    this.filters = { searchText: '' };
    this.items = [];
    this.paginationInvoiceTenant = paginationSchema;
    this.summaryInvoiceTenant = {
      paidAmount: 0,
      outstandingAmount: 0,
      paidCount: 0,
      averageDaysToPay: "",
    };

    makeObservable(this, {
      selected: observable,
      filters: observable,
      items: observable,
      paginationInvoiceTenant: observable,
      summaryInvoiceTenant: observable,
      setSelected: action,
      setItems: action,
      setFilters: action,
      setPaginationInvoiceTenant: action,
      fetch: flow,
      fetchOne: flow,
      fetchTenantInvoice: flow,
      create: flow,
      batchCreate: flow,
      update: flow,
      updateOccupantInvoices: flow,
      voidInvoicesOfOccupant: flow,
      capturePayment: flow,
      deletePayment: flow,
      delete: flow,
      deleteCanceledTenantInvoices: flow,
      deleteInvoicesOfOccupant: flow,
    });
  }

  get filteredItems() {
    let filteredItems = this.items;

    if (this.filters.searchText) {
      const regExp = /\s|\.|-/gi;
      const cleanedSearchText = this.filters.searchText.toLowerCase().replace(regExp, '');
      filteredItems = filteredItems.filter(({ _id, occupantId }) => {
        let found =
          occupantId.replace(regExp, '').toLowerCase().indexOf(cleanedSearchText) !== -1;
        return found;
      });
    }

    return filteredItems;
  }

  setSelected = (invoice) => (this.selected = invoice);
  setItems = (invoices) => (this.items = invoices);

  setFilters = ({ searchText = '' }) => (this.filters = { searchText });

  setPaginationInvoiceTenant = ({ page, limit } = {}) => {
    const { page: currentPage, pageSize: currentPageSize } = this.paginationInvoiceTenant;
    this.paginationInvoiceTenant = {
      ...this.paginationInvoiceTenant,
      page: page ?? currentPage,
      pageSize: limit ?? currentPageSize,
    };
  };

  *fetch() {
    try {
      const response = yield apiFetcher().get('/invoices');
      this.items = response.data;
      if (this.selected._id) {
        this.setSelected(this.items.find((item) => item._id === this.selected._id) || {});
      }
      return { status: 200, data: response.data };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *fetchOne(invoiceId) {
    try {
      const response = yield apiFetcher().get(`/invoices/${invoiceId}`);
      const updatedInvoice = response.data;
      this.items = updateItems(updatedInvoice, this.items);
      if (this.selected?._id === updatedInvoice._id) {
        this.setSelected(updatedInvoice);
      }
      return { status: 200, data: updatedInvoice };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *fetchTenantInvoice(tenantId, options = {}) {
    try {
      const { usePagination = false } = options;

      // Build request params conditionally
      const params = {};
      if (usePagination) {
        params.page = this.paginationInvoiceTenant.page;
        params.limit = this.paginationInvoiceTenant.pageSize;
      }

      const response = yield apiFetcher().get(`/invoices/tenant/${tenantId}`, {
        params: Object.keys(params).length > 0 ? params : undefined
      });

      // Handle both paginated and non-paginated responses
      const hasPagination = response.data?.pagination !== undefined;
      const invoicesData =
        Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data)
            ? response.data
            : [];

      // Update items
      this.items = invoicesData;

      // Update pagination only if response contains pagination data
      if (hasPagination) {
        const pagination = response.data.pagination;
        this.paginationInvoiceTenant = {
          page: pagination.page ?? this.paginationInvoiceTenant.page,
          pageSize: pagination.limit ?? this.paginationInvoiceTenant.pageSize,
          totalPages: pagination.pages ?? this.paginationInvoiceTenant.totalPages,
          totalData: pagination.total ?? this.paginationInvoiceTenant.totalData,
          hasNextPage: pagination.hasNextPage ?? false,
          hasPrevPage: pagination.hasPrevPage ?? false,
        };
      }

      // Update summary if available
      if (response.data?.summary) {
        const summary = response.data.summary;
        this.summaryInvoiceTenant = {
          paidAmount: summary.paidAmount ?? this.summaryInvoiceTenant.paidAmount,
          outstandingAmount: summary.outstandingAmount ?? this.summaryInvoiceTenant.outstandingAmount,
          paidCount: summary.paidCount ?? this.summaryInvoiceTenant.paidCount,
          averageDaysToPay: summary.averageDaysToPay ?? this.summaryInvoiceTenant.averageDaysToPay,
        };
      }

      // Return appropriate data structure
      const returnData = hasPagination ? response.data : invoicesData;
      return { status: 200, data: returnData };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *create(invoice) {
    try {
      const response = yield apiFetcher().post('/invoices', invoice);
      const createdInvoice = response.data;
      this.items = updateItems(createdInvoice, this.items);

      return { status: 200, data: createdInvoice };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *batchCreate({ occupantId, quantity }) {
    try {
      const response = yield api.post('/invoices/batch', { occupantId, quantity });
      const createdInvoices = response.data;
      this.items = improvedUpdateItems(createdInvoices, this.items);

      return { status: 200, data: createdInvoices };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *update(invoice) {
    try {
      const response = yield apiFetcher().patch(`/invoices/${invoice._id}`, invoice);
      const updatedInvoice = response.data;
      this.items = updateItems(updatedInvoice, this.items);
      if (this.selected?._id === updatedInvoice._id) {
        this.setSelected(updatedInvoice);
      }

      return { status: 200, data: updatedInvoice };
    } catch (error) {
      return { status: error?.response?.status, message: error?.response?.data.message };
    }
  }

  *voidInvoicesOfOccupant(tenantId) {
    try {
      yield apiFetcher().patch(`invoices/set-void/tenant/${tenantId}`);
      return { status: 200 };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *updateOccupantInvoices(tenantId, query) {
    try {
      yield apiFetcher().patch(`/invoices/tenant/${tenantId}${query}`);
      return { status: 200 };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *capturePayment(invoice) {
    try {
      const response = yield apiFetcher().patch(`/invoices/${invoice._id}/capture`, invoice.payment)
      const updatedInvoice = response.data;
      this.items = updateItems(updatedInvoice, this.items);
      return { status: 200, data: updatedInvoice };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *deletePayment(invoice) {
    try {
      const response = yield apiFetcher().patch(`/invoices/${invoice._id}/delete/${invoice.transactionId}`)
      const updatedInvoice = response.data
      this.items = updateItems(updatedInvoice, this.items)
      return { status: 200 };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *delete(ids) {
    try {
      yield apiFetcher().delete(`/invoices/${ids.join(',')}`);
      return { status: 200 };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *deleteCanceledTenantInvoices(tenantId) {
    try {
      yield apiFetcher().delete(`/invoices/tenant/${tenantId}`);
      return { status: 200 };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *deleteInvoicesOfOccupant(tenantId, query) {
    try {
      // TODO: yield api
      yield apiFetcher().delete(`/invoices/tenant/${tenantId}?${query}`)
      return { status: 200 };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }
}
